import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * L'ELENCO dei minigiochi, e il punto dove ognuno entra o non entra (bottone flottante nel guscio, I-909; Luca, L-3508 → (c), I-964,
 * 20/10: «bottone flottante su ogni schermata»; reference `services/qlash-telegram-app/ui/2026-10-20-bottone-minigiochi.md` del KB).
 *
 * 🔴 OGNI VOCE PORTA IL PROPRIO FLAG LETTERALE (`import.meta.env.VITE_FEATURE_<GIOCO>_ENABLED === 'true'`), dentro la voce e non in una variabile: il bundler
 * piega il confronto e toglie anche i METADATI della voce (titolo, rotta, icona). Con un solo flag spento l'elenco e' `[]`, e con `[]` il bottone non si
 * disegna ne' il suo codice entra nel bundle (`MinigiochiGuscio` e' `null`, v. sotto): ADR-0229 §5, «in produzione non si vede niente». Il confronto va scritto cosi', MAI
 * dentro una funzione d'appoggio: una chiamata non si piega, il bundler tiene l'argomento (stessa trappola di `limuloDevExternalToken`, CLAUDE.md).
 * ⚠️ `env.featureSolitarioEnabled` (la seconda lettura) NON serve qui: decide se le ROTTE esistono, e il bottone le raggiunge solo con Limulo acceso
 * (`richiedeLimulo`), la stessa serratura del router.
 *
 * Per aggiungere un gioco (il 2048, di q2048): UNA voce in `elencoMinigiochi()` col suo flag letterale, e lo STESSO flag in OR nella condizione di
 * `MinigiochiGuscio` (qui sotto, stesso file). Il punto di montaggio e' UNO, `AppShell`, e non si tocca.
 */
export interface Minigioco {
  /** Chiave stabile (data-attribute del foglio, `key` di React). */
  readonly id: string;
  /** Chiave i18n del TITOLO, con il suo namespace (`solitario:titolo`): il nome di un gioco e' di chi lo possiede. */
  readonly titolo: string;
  /** Chiave i18n del sottotitolo. */
  readonly sottotitolo: string;
  /** Icona del gioco: un file di `public/`. ⚠️ PROVVISORIA (le definitive sono fuori da questa PR). */
  readonly icona: string;
  /** La rotta a cui porta il tocco (una rotta che il flag del gioco crea). */
  readonly rotta: string;
  /** Solo per chi ha il ruolo `beta` (ADR-0229 §5: ogni gioco della TMA sta dietro un flag letterale E RoleGate beta). */
  readonly richiedeBeta: boolean;
  /** La rotta esiste solo con Limulo acceso (il router la crea in AND col flag): senza, il tocco porterebbe nel vuoto. */
  readonly richiedeLimulo: boolean;
}

export function elencoMinigiochi(): readonly Minigioco[] {
  return [
    ...(import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true'
      ? [
          {
            id: 'solitario',
            titolo: 'solitario:titolo',
            sottotitolo: 'solitario:intro',
            icona: '/games/solitario.svg',
            rotta: '/solitario',
            richiedeBeta: true,
            richiedeLimulo: true,
          } satisfies Minigioco,
        ]
      : []),
    // Il 2048 gioca in locale (anteprima di collaudo): la rotta `/2048` non passa da Limulo, quindi qui `richiedeLimulo` e' falso.
    ...(import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'
      ? [
          {
            id: 'q2048',
            titolo: 'q2048:titolo',
            sottotitolo: 'q2048:intro',
            icona: '/games/2048.svg',
            rotta: '/2048',
            richiedeBeta: true,
            richiedeLimulo: false,
          } satisfies Minigioco,
        ]
      : []),
  ];
}

/** Le voci che QUESTO giocatore vede: ruolo e Limulo. Pura, perche' il banco e i test la esercitano senza il resto del guscio. */
export function vociVisibili(
  voci: readonly Minigioco[],
  chi: { readonly beta: boolean; readonly limulo: boolean },
): readonly Minigioco[] {
  return voci.filter((v) => (!v.richiedeBeta || chi.beta) && (!v.richiedeLimulo || chi.limulo));
}

/**
 * Il guscio dei minigiochi (bottone + foglio), caricato SOLO se almeno un gioco ha il flag acceso. In produzione e' `null` e il chunk non esce dalla build:
 * `AppShell` lo monta UNA volta e basta (`tools/verify-minigiochi.ts`).
 *
 * 🔴 LA CONDIZIONE E' LA STESSA DELL'ELENCO, scritta una seconda volta, e NON si ricava dall'elenco: provato il 20/10 con `elencoMinigiochi().length > 0`, il
 * bundler NON lo piega (il chiamante di una funzione non e' una costante) e il chunk usciva in produzione, col bottone dentro. Un confronto letterale per
 * ogni gioco, in OR (il 2048 aggiunge `|| import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'`), e `verify-minigiochi` pretende che i flag qui e nell'elenco
 * siano gli stessi: una voce nell'elenco senza il suo flag qui non si vedrebbe mai, e il contrario caricherebbe un chunk vuoto.
 */
export const MinigiochiGuscio: LazyExoticComponent<ComponentType> | null =
  import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true' || import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'
    ? lazy(() => import('@/components/minigiochi/MinigiochiGuscio'))
    : null;

/**
 * La PAGINA `/minigiochi` della v3 (ADR-0264 §2), caricata SOLO se almeno un gioco ha il flag acceso: la STESSA condizione dell'elenco e del guscio, scritta una terza volta con i flag LETTERALI (stessa trappola:
 * una chiamata non si piega). Qui non c'e' il flag della v3: lo aggiunge `lib/v3.ts` (`PaginaMinigiochiV3`, l'unico file che lo legge, e che importa gia' questo: l'inverso farebbe un ciclo). Il router usa quella,
 * mai questa: in produzione, coi flag spenti, non c'e' ne' la rotta ne' il chunk.
 */
export const PaginaMinigiochi: LazyExoticComponent<ComponentType> | null =
  import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true' || import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'
    ? lazy(() => import('@/routes/minigiochi/MinigiochiPagina').then((m) => ({ default: m.MinigiochiPagina })))
    : null;
